/**
 * reconteo-decisions -- Modo top-level del ActionArea para revisar las discrepancias
 * que Argemiro detecta tras analizar un conteo de inventario (Argemiro Reconteo
 * Inteligente V1). Renderiza una LISTA navegable de casos con discrepancia y, por
 * cada caso, un detalle con el mensaje del agente + la tabla numerica + 3 decisiones
 * (Volver a contar / Justificar / Escalar admin).
 *
 * Mision: ARGEMIRO-RECONTEO-INTELIGENTE-F1-001 / T4.
 * Spec UI: Docs/6_Proyectos/ARGEMIRO-RECONTEO-V1/SPEC_V1_CONGELADO.md secciones 3, 4, 7.
 *
 * El mensaje del agente viene listo desde el workflow n8n (no hay LLM clasificador
 * en el panel). El componente solo renderiza e instrumenta las decisiones.
 *
 * Payload (showMode('reconteo-decisions', payload)):
 *   - businessId            (string, requerido)
 *   - userId                (string, opcional - contexto; el POST decision usa log_id)
 *   - userName              (string, opcional - se muestra en el saludo)
 *   - analizados            (number, opcional - total de insumos analizados)
 *   - ok                    (number, opcional - insumos sin discrepancia)
 *   - discrepancias         (array, requerido) - casos con discrepancia. Cada uno:
 *       { log_id, insumo_nombre, stock_sistema, conteo_real, diff_signed,
 *         valor_diff?, mensaje_argemiro, severidad?, unidad? }
 *       log_id = uuid de la fila verificacion_discrepancia_log creada por el
 *       webhook /reconteo/analyze (decision=null hasta que el operador decida).
 *   - decisionWebhookUrl    (string, opcional) - webhook n8n /reconteo/decision.
 *                           T5 lo cablea con la URL real; default placeholder.
 *   - onDecisionsComplete   (function, opcional) callback({ casos }) cuando TODAS
 *                           las discrepancias quedan decididas.
 *   - onCasoDecidido        (function, opcional) callback({ caso }) por cada decision.
 *
 * Eventos emitidos a ActionArea (ctx.emit):
 *   - 'mode-action': { mode:'reconteo-decisions', action:'decision'|'completed', data }
 *
 * @module presentation/components/ActionAreaModes/reconteo-decisions
 */

import { ReconteoApiClient } from '../../../infrastructure/api/ReconteoApiClient.js';
import { fetchQueCambia } from '../../../infrastructure/api/ApiBase.js';
import { initSubConteos, subtotalFila, totalEstado } from '../SubEstadosConfig.js';

const DEFAULT_DECISION_WEBHOOK = 'https://n8n.nexodigital360.com/webhook/reconteo/decision';

// Maximo de intentos de reconteo por caso (mirror de MAX_INTENTOS en ReconteoApiClient).
// Cuando reconteosIntentos === MAX_INTENTOS el operador no puede volver a contar
// y solo le quedan Justificar / Escalar.
const MAX_INTENTOS = 2;

// Motivos tipados de la jerarquia Josse (SPEC seccion 3 escenario B).
const MOTIVOS = [
  { value: 'compra_pendiente', label: 'Compra del dia no ingresada al sistema' },
  // MV-T2 (bit #3349, canon Josse 21-jul): la palabra "Merma" desaparece de la UI.
  // El value sigue siendo 'merma' a proposito: viaja como motivo_tipado al webhook
  // n8n de decisiones (contrato ajeno a este PR) -- solo cambia el label visible.
  { value: 'merma',            label: 'Desperdicio / producto vencido' },
  { value: 'dano',             label: 'Dano / producto roto' },
  { value: 'otro',             label: 'Otro motivo' }
];

// Estados de decision de un caso + su presentacion visual.
const DECISION_META = {
  pendiente:     { label: 'Pendiente',   color: '#d29922', bg: '#d2992222' },
  recontar:      { label: 'A recontar',  color: '#58a6ff', bg: '#1f6feb22' },
  aceptar:       { label: 'Justificado', color: '#3fb950', bg: '#23863622' },
  pending_admin: { label: 'Escalado',    color: '#a371f7', bg: '#a371f722' }
};

// OPS-RECONTEO-P2 (bit #3293): el backend /reconteo/recount devuelve 409 si la
// decision del caso ya es terminal (contrato documentado en ReconteoApiClient.js:
// "409 -- decision ya terminal (aceptar / pending_admin)"). El boton "Volver a
// contar" solo miraba intentosRestantes, no si el caso ya estaba escalado/aceptado
// -- un operador que escalaba SIN agotar intentos (ej. escala en el primer vistazo)
// seguia viendo el boton, lo clickeaba, y el backend rechazaba con 409 mostrado
// como "sin conexion con el servidor" (mensaje generico, engañoso).
function esDecisionTerminal(decision) {
  return decision === 'aceptar' || decision === 'pending_admin';
}

function fmtNum(n) {
  if (n === null || n === undefined || isNaN(Number(n))) return '0';
  return Number(n).toLocaleString('es-CO', { maximumFractionDigits: 3 });
}

// Anti-fraude (SPEC reconteo a ciegas): el operador ve solo la DIRECCION de la
// diferencia (falta / sobra), nunca la cifra ni el stock del sistema, para que no
// pueda ajustar su conteo.
//
// CONTRATO DE SIGNO (bit #3180/#3191, REC-T2): formula oficial en
// Docs/6_Proyectos/ARGEMIRO-RECONTEO-V1/SPEC_V1_CONGELADO.md seccion 2
// (aprobada Josse+Ares, rev 2026-05-15):
//   diff = inventario_actual_sistema - conteo_real
// Si el operador conto MENOS que el sistema (conteo_real < sistema) -> falta
// stock fisico -> diff = sistema - conteo_real = POSITIVO.
// Si conto MAS que el sistema (conteo_real > sistema) -> sobra stock -> diff
// = sistema - conteo_real = NEGATIVO.
// => diff_signed > 0 = FALTA, diff_signed < 0 = SOBRA. (Version previa tenia
// esto invertido -- el backend n8n /reconteo/analyze DEBE calcular diff_signed
// con esta MISMA formula/signo; si el nodo n8n usa otra convencion, este
// contrato queda roto de nuevo del otro lado.)
function diffDireccion(diffSigned) {
  const n = Number(diffSigned);
  if (n > 0) return { icon: '↓', label: 'Falta', frase: 'Hay una diferencia: falta', color: '#f85149' };
  if (n < 0) return { icon: '↑', label: 'Sobra', frase: 'Hay una diferencia: sobra', color: '#d29922' };
  return { icon: '•', label: 'Diferencia', frase: 'Se detecto una diferencia', color: '#8b949e' };
}

// Label corto de unidad fisica del insumo. Default vacio (sin unidad).
function unidadLabel(u) {
  if (!u) return '';
  const x = String(u).trim().toLowerCase();
  if (x === 'g' || x.startsWith('gramo')) return 'g';
  if (x === 'u' || x.startsWith('unidad')) return 'u';
  return String(u).trim();
}

// Sub-estados pueden venir como strings o como objetos {label, nombre, id}.
// Normaliza el display textual del label.
function subEstadoLabel(s) {
  if (typeof s === 'string') return s;
  if (s && typeof s === 'object') return s.label || s.nombre || String(s.id || '');
  return String(s);
}

export function renderReconteoDecisions(container, payload, ctx) {
  const businessId = payload?.businessId || '';
  const userId = payload?.userId || '';
  const userName = payload?.userName || 'colaborador';
  const analizados = Number(payload?.analizados ?? 0);
  const okCount = Number(payload?.ok ?? 0);
  const discrepancias = Array.isArray(payload?.discrepancias) ? payload.discrepancias : [];
  const decisionWebhookUrl = payload?.decisionWebhookUrl || DEFAULT_DECISION_WEBHOOK;
  const onDecisionsComplete = typeof payload?.onDecisionsComplete === 'function' ? payload.onDecisionsComplete : null;
  const onCasoDecidido = typeof payload?.onCasoDecidido === 'function' ? payload.onCasoDecidido : null;

  if (!businessId || discrepancias.length === 0) {
    container.innerHTML = `
      <div style="padding:18px;color:#d29922;font-size:0.9rem">
        reconteo-decisions: payload incompleto (businessId + discrepancias[] requeridos).
      </div>
    `;
    expandContainer(container);
    return;
  }

  // Estado mutable: cada caso arranca 'pendiente' y guarda la decision + justificacion.
  // logId = uuid del log row que el webhook analyze ya inserto; el POST decision
  // lo referencia para el UPDATE. insumoId se conserva solo como dato informativo.
  const casos = discrepancias.map((d) => ({
    logId: d.log_id ?? d.logId ?? null,
    insumoId: d.insumoId ?? d.insumo_id ?? null,
    nombre: d.insumo_nombre ?? d.nombre ?? '(sin nombre)',
    stockSistema: Number(d.stock_sistema ?? 0),
    conteoReal: Number(d.conteo_real ?? 0),
    diffSigned: Number(d.diff_signed ?? 0),
    valorDiff: (d.valor_diff !== null && d.valor_diff !== undefined) ? Number(d.valor_diff) : null,
    diffPct: (d.diff_pct !== null && d.diff_pct !== undefined) ? Number(d.diff_pct) : null,
    mensaje: d.mensaje_argemiro ?? d.mensaje ?? '',
    severidad: d.severidad ?? 'media',
    unidad: unidadLabel(d.unidad ?? d.unidad_conteo ?? ''),
    reconteosIntentos: Number(d.reconteos_intentos ?? d.reconteosIntentos ?? 0),
    intentosRestantes: Math.max(0, MAX_INTENTOS - Number(d.reconteos_intentos ?? d.reconteosIntentos ?? 0)),
    reconteos: Array.isArray(d.reconteos) ? d.reconteos.map(r => ({ ...r })) : [],
    conteoRecontado: null,
    subEstados: Array.isArray(d.sub_estados) ? d.sub_estados : (Array.isArray(d.subEstados) ? d.subEstados : []),
    // REC-T5 (bit #3180): valores del conteo ORIGINAL para precargar el reconteo
    // como default (decision Josse 2026-07-18) -- enriquecidos client-side por
    // InventarioView (n8n nunca tuvo este desglose, solo el total).
    conteoOriginalSubConteos: d.conteo_original_sub_conteos ?? null,
    conteoOriginalSimple: (d.conteo_original_simple !== undefined && d.conteo_original_simple !== null) ? Number(d.conteo_original_simple) : null,
    decision: 'pendiente',
    motivo: null,
    justificacion: ''
  }));

  // Vista actual: 'lista' | 'detalle' | 'justificar'. currentIdx = caso activo.
  let currentView = 'lista';
  let currentIdx = 0;

  expandContainer(container);
  render();

  function decididos() {
    return casos.filter(c => c.decision !== 'pendiente').length;
  }

  function todasDecididas() {
    return casos.every(c => c.decision !== 'pendiente');
  }

  function render() {
    if (currentView === 'detalle') renderDetalle();
    else if (currentView === 'justificar') renderJustificar();
    else if (currentView === 'reconteo-inline') renderReconteoInline();
    else renderLista();
  }

  // ============== Vista LISTA ==============

  function renderLista() {
    const n = casos.length;
    const hechas = decididos();
    const saludo = `Termine de analizar tu conteo${analizados > 0 ? ` de ${analizados} insumos` : ''}. `
      + `Encontre ${n} ${n === 1 ? 'insumo' : 'insumos'} con diferencias significativas. `
      + `${n === 1 ? 'Lo revisamos?' : 'Vamos uno por uno?'}`;

    // Layout 2 lineas por caso: nombre + badge arriba, diff + boton abajo. Robusto en
    // kiosk movil (375px) donde un layout 1-fila comprimia el nombre del insumo.
    const casosHtml = casos.map((c, i) => {
      const dm = DECISION_META[c.decision] || DECISION_META.pendiente;
      // Anti-fraude: la lista muestra solo la direccion (falta/sobra), nunca la cifra.
      const dir = diffDireccion(c.diffSigned);
      return `
        <div data-rec-caso="${i}" style="padding:9px 11px;border:1px solid #21262d;border-radius:6px;margin-bottom:6px;background:#0d1117">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
            <span style="min-width:24px;height:22px;display:inline-flex;align-items:center;justify-content:center;background:#21262d;color:#8b949e;border-radius:4px;font-size:0.875rem;font-weight:700;font-family:monospace">${i + 1}</span>
            <span style="flex:1;min-width:0;font-size:0.875rem;color:#f0f6fc;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${ctx.escHtml(c.nombre)}</span>
            <span style="padding:2px 8px;background:${dm.bg};color:${dm.color};border-radius:9px;font-size:0.875rem;white-space:nowrap">${dm.label}</span>
          </div>
          <div style="display:flex;align-items:center;justify-content:space-between;gap:8px">
            <span style="font-size:0.875rem;font-weight:600;color:${dir.color}">${dir.icon} ${dir.label}</span>
            <button data-rec-revisar="${i}" type="button"
                    style="padding:4px 14px;background:#21262d;border:1px solid #30363d;border-radius:5px;color:#c9d1d9;cursor:pointer;font-size:0.875rem;white-space:nowrap">
              ${c.decision === 'pendiente' ? 'Revisar' : 'Ver'}
            </button>
          </div>
        </div>
      `;
    }).join('');

    const completas = todasDecididas();

    container.innerHTML = `
      <div class="aa-reconteo" data-aa-mode="reconteo-decisions">
        <div style="padding:12px 16px 8px">
          <div style="display:flex;gap:8px;align-items:flex-start;margin-bottom:12px">
            <span style="font-size:1.05rem;line-height:1.2">🤖</span>
            <div style="font-size:0.875rem;color:#c9d1d9;line-height:1.5;background:#161b22;border:1px solid #21262d;border-radius:8px;padding:8px 11px;flex:1">
              Hola ${ctx.escHtml(userName)}, ${ctx.escHtml(saludo)}
            </div>
          </div>

          <div style="font-size:0.875rem;color:#8b949e;margin-bottom:6px">Discrepancias detectadas (${casos.length}):</div>
          <div data-rec-lista style="max-height:260px;overflow-y:auto">
            ${casosHtml}
          </div>
          ${okCount > 0 ? `<div style="margin-top:8px;font-size:0.875rem;color:#3fb950">✓ Sin discrepancia: ${okCount} ${okCount === 1 ? 'insumo' : 'insumos'} OK</div>` : ''}
        </div>

        <footer style="display:flex;align-items:center;justify-content:space-between;gap:8px;padding:10px 16px 14px;border-top:1px solid #21262d">
          <div style="font-size:0.875rem;color:${completas ? '#3fb950' : '#8b949e'}">
            ${hechas}/${casos.length} ${completas ? '· todas decididas ✓' : 'decididas'}
          </div>
          <div style="display:flex;gap:8px">
            <button data-rec-wizard type="button"
                    style="padding:6px 14px;background:transparent;border:1px solid #30363d;border-radius:5px;color:#c9d1d9;cursor:pointer;font-size:0.875rem">
              Revisar ${hechas > 0 ? 'pendientes' : 'todas en orden'}
            </button>
            <button data-rec-finish type="button" ${completas ? '' : 'disabled'}
                    style="padding:6px 16px;background:${completas ? '#238636' : '#30363d'};border:1px solid ${completas ? '#238636' : '#30363d'};border-radius:5px;color:#fff;cursor:${completas ? 'pointer' : 'not-allowed'};font-size:0.875rem">
              Listo
            </button>
          </div>
        </footer>
      </div>
    `;

    casos.forEach((_, i) => {
      const $row = container.querySelector(`[data-rec-revisar="${i}"]`);
      if ($row) $row.addEventListener('click', () => { currentIdx = i; currentView = 'detalle'; render(); });
    });
    container.querySelector('[data-rec-wizard]').addEventListener('click', () => {
      // Salta al primer caso pendiente; si no hay, al primero.
      const next = casos.findIndex(c => c.decision === 'pendiente');
      currentIdx = next >= 0 ? next : 0;
      currentView = 'detalle';
      render();
    });
    const $finish = container.querySelector('[data-rec-finish]');
    if ($finish && todasDecididas()) {
      $finish.addEventListener('click', finish);
    }
  }

  // ============== Vista DETALLE individual ==============

  function renderDetalle() {
    const c = casos[currentIdx];
    const dm = DECISION_META[c.decision] || DECISION_META.pendiente;
    const dir = diffDireccion(c.diffSigned);
    const u = c.unidad ? ' ' + c.unidad : '';
    // Mensaje del agente: viene del workflow n8n. Fallback si llega vacio.
    const mensaje = c.mensaje
      || `En ${c.nombre} veo una diferencia entre el sistema y tu conteo. Antes de aceptarla, podrias revisar? A veces el insumo quedo contado en otra zona o en otro estado.`;

    // Orden Josse (SPEC seccion 4): mensaje del agente PRIMERO, tabla numerica DESPUES.
    container.innerHTML = `
      <div class="aa-reconteo" data-aa-mode="reconteo-decisions">
        <div style="padding:12px 16px 4px;font-size:0.875rem;color:#8b949e">Caso ${currentIdx + 1} de ${casos.length}
          <span style="padding:1px 7px;background:${dm.bg};color:${dm.color};border-radius:8px;margin-left:6px">${dm.label}</span>
        </div>

        <div style="padding:8px 16px 8px">
          <div style="font-size:0.875rem;color:#f0f6fc;font-weight:600;margin-bottom:10px">📦 ${ctx.escHtml(c.nombre)}</div>

          <div style="display:flex;gap:8px;align-items:flex-start;margin-bottom:12px">
            <span style="font-size:1.05rem;line-height:1.2">🤖</span>
            <div style="font-size:0.875rem;color:#c9d1d9;line-height:1.5;background:#161b22;border:1px solid #21262d;border-radius:8px;padding:9px 11px;flex:1">
              ${ctx.escHtml(mensaje)}
            </div>
          </div>

          <div style="background:#0d1117;border:1px solid #30363d;border-radius:6px;padding:10px 12px;margin-bottom:14px">
            <div style="display:flex;justify-content:space-between;font-size:0.875rem;color:#c9d1d9;padding:2px 0">
              <span style="color:#8b949e">Tu conteo</span><strong style="color:#f0f6fc">${fmtNum(c.conteoReal)}${u}</strong>
            </div>
            <div style="border-top:1px solid #21262d;margin:7px 0"></div>
            <div style="display:flex;align-items:center;gap:7px;font-size:0.875rem;color:${dir.color};padding:3px 0">
              <span style="font-size:1rem;line-height:1">${dir.icon}</span>
              <span>${dir.frase}</span>
            </div>
          </div>

          <div data-rec-actions>
            <div style="font-size:0.875rem;color:#8b949e;margin-bottom:8px">Como quieres proceder?</div>
            <div style="display:flex;gap:6px;flex-wrap:wrap">
              ${(c.intentosRestantes > 0 && !esDecisionTerminal(c.decision)) ? `
              <button data-rec-recontar type="button"
                      style="flex:1;min-width:120px;padding:7px 10px;background:#1f6feb22;border:1px solid #1f6feb;border-radius:5px;color:#58a6ff;cursor:pointer;font-size:0.875rem">
                ↻ Volver a contar
              </button>
              ` : ''}
              <button data-rec-justificar type="button"
                      style="flex:1;min-width:120px;padding:7px 10px;background:#23863622;border:1px solid #238636;border-radius:5px;color:#3fb950;cursor:pointer;font-size:0.875rem">
                ✎ Justificar
              </button>
              <button data-rec-escalar type="button" ${c.decision === 'pending_admin' ? 'disabled' : ''}
                      style="flex:1;min-width:120px;padding:7px 10px;background:${c.decision === 'pending_admin' ? '#30363d' : '#a371f722'};border:1px solid ${c.decision === 'pending_admin' ? '#30363d' : '#a371f7'};border-radius:5px;color:${c.decision === 'pending_admin' ? '#6e7681' : '#a371f7'};cursor:${c.decision === 'pending_admin' ? 'not-allowed' : 'pointer'};font-size:0.875rem">
                ⇧ ${c.decision === 'pending_admin' ? 'Ya escalado' : 'Escalar admin'}
              </button>
            </div>
            ${c.decision === 'pending_admin' ? `
              <div style="margin-top:8px;font-size:0.875rem;color:${dm.color}">
                Decision actual: ${dm.label}. Este caso ya fue escalado al administrador -- no se puede recontar ni volver a escalar. Puedes justificarlo si quieres agregar contexto.
              </div>` : c.decision !== 'pendiente' ? `
              <div style="margin-top:8px;font-size:0.875rem;color:${dm.color}">
                Decision actual: ${dm.label}${c.motivo ? ` · ${ctx.escHtml(motivoLabel(c.motivo))}` : ''}. Puedes cambiarla.
              </div>` : ''}
          </div>
          <div data-rec-status style="margin-top:8px;font-size:0.875rem;min-height:1em"></div>
        </div>

        <footer style="display:flex;align-items:center;justify-content:space-between;gap:8px;padding:10px 16px 14px;border-top:1px solid #21262d">
          <button data-rec-prev type="button" ${currentIdx === 0 ? 'disabled' : ''}
                  style="padding:6px 12px;background:transparent;border:1px solid #30363d;border-radius:5px;color:${currentIdx === 0 ? '#484f58' : '#c9d1d9'};cursor:${currentIdx === 0 ? 'not-allowed' : 'pointer'};font-size:0.875rem">
            ← Anterior
          </button>
          <button data-rec-tolist type="button"
                  style="padding:6px 12px;background:transparent;border:1px solid #30363d;border-radius:5px;color:#c9d1d9;cursor:pointer;font-size:0.875rem">
            Lista
          </button>
          <button data-rec-next type="button" ${currentIdx >= casos.length - 1 ? 'disabled' : ''}
                  style="padding:6px 12px;background:transparent;border:1px solid #30363d;border-radius:5px;color:${currentIdx >= casos.length - 1 ? '#484f58' : '#c9d1d9'};cursor:${currentIdx >= casos.length - 1 ? 'not-allowed' : 'pointer'};font-size:0.875rem">
            Siguiente →
          </button>
        </footer>
      </div>
    `;

    container.querySelector('[data-rec-tolist]').addEventListener('click', () => { currentView = 'lista'; render(); });
    // T4+T5 RECONTEO-INLINE-F1-4-001: "Volver a contar" abre la vista inline (T4) y
    // el state machine post-confirm decide auto-accept / retry / agotado (T5). Si
    // c.intentosRestantes === 0 el boton no se renderea -- solo Justificar/Escalar.
    container.querySelector('[data-rec-recontar]')?.addEventListener('click', () => { currentView = 'reconteo-inline'; render(); });
    container.querySelector('[data-rec-justificar]').addEventListener('click', () => { currentView = 'justificar'; render(); });
    container.querySelector('[data-rec-escalar]').addEventListener('click', () => decidir('pending_admin'));

    const $prev = container.querySelector('[data-rec-prev]');
    const $next = container.querySelector('[data-rec-next]');
    if (currentIdx > 0) $prev.addEventListener('click', () => { currentIdx--; render(); });
    if (currentIdx < casos.length - 1) $next.addEventListener('click', () => { currentIdx++; render(); });
  }

  // ============== Vista JUSTIFICAR (dropdown motivo + textarea) ==============

  function renderJustificar() {
    const c = casos[currentIdx];
    const motivoOpts = [
      `<option value="">-- Selecciona el motivo --</option>`,
      ...MOTIVOS.map(m => `<option value="${m.value}"${c.motivo === m.value ? ' selected' : ''}>${ctx.escHtml(m.label)}</option>`)
    ].join('');

    container.innerHTML = `
      <div class="aa-reconteo" data-aa-mode="reconteo-decisions">
        <div style="padding:12px 16px 4px;font-size:0.875rem;color:#8b949e">Justificar · ${ctx.escHtml(c.nombre)} · caso ${currentIdx + 1} de ${casos.length}</div>

        <div style="padding:10px 16px 8px">
          <div style="font-size:0.875rem;color:#8b949e;margin-bottom:4px">Motivo de la diferencia</div>
          <select data-rec-motivo style="width:100%;padding:7px 9px;background:#0d1117;border:1px solid #30363d;border-radius:5px;color:#c9d1d9;font-size:0.875rem;margin-bottom:12px">
            ${motivoOpts}
          </select>

          <div style="font-size:0.875rem;color:#8b949e;margin-bottom:4px">Detalle (opcional)</div>
          <textarea data-rec-justif rows="3" placeholder="Agrega contexto para el admin si lo consideras util..."
            style="width:100%;box-sizing:border-box;padding:7px 9px;background:#0d1117;border:1px solid #30363d;border-radius:5px;color:#c9d1d9;font-size:0.875rem;resize:vertical;font-family:inherit">${ctx.escHtml(c.justificacion || '')}</textarea>

          <div data-rec-status style="margin-top:8px;font-size:0.875rem;min-height:1em"></div>
        </div>

        <footer style="display:flex;align-items:center;justify-content:flex-end;gap:8px;padding:10px 16px 14px;border-top:1px solid #21262d">
          <button data-rec-cancel type="button"
                  style="padding:6px 14px;background:transparent;border:1px solid #30363d;border-radius:5px;color:#c9d1d9;cursor:pointer;font-size:0.875rem">
            Cancelar
          </button>
          <button data-rec-confirm type="button"
                  style="padding:6px 16px;background:#238636;border:1px solid #238636;border-radius:5px;color:#fff;cursor:pointer;font-size:0.875rem">
            ✓ Confirmar justificacion
          </button>
        </footer>
      </div>
    `;

    const $motivo = container.querySelector('[data-rec-motivo]');
    const $justif = container.querySelector('[data-rec-justif]');
    $motivo.addEventListener('change', (e) => { c.motivo = e.target.value || null; });
    $justif.addEventListener('input', (e) => { c.justificacion = e.target.value; });

    container.querySelector('[data-rec-cancel]').addEventListener('click', () => { currentView = 'detalle'; render(); });
    container.querySelector('[data-rec-confirm]').addEventListener('click', () => {
      if (!c.motivo) {
        const $st = container.querySelector('[data-rec-status]');
        if ($st) { $st.style.color = '#d29922'; $st.textContent = 'Selecciona un motivo antes de confirmar.'; }
        return;
      }
      decidir('aceptar');
    });
  }

  // ============== Vista RECONTEO INLINE (a ciegas, T4) ==============

  // REC-T5 (bit #3180): tamano config del estado, extraido del objeto enriquecido
  // (o null si vino como string plano legado -- cae a tipo simple).
  function estadoTamanoConfig(s) {
    return (s && typeof s === 'object' && s.tamano !== undefined && s.tamano !== null) ? Number(s.tamano) : null;
  }
  function estadoTipo(s) {
    return (s && typeof s === 'object' && s.tipo === 'porcionado' && estadoTamanoConfig(s) !== null) ? 'porcionado' : 'simple';
  }

  // REC-T5: pinta 1 sub-estado con sus filas (mismo patron visual que
  // InventarioView.renderTable para el estado tipo=porcionado -- tamano fijo del
  // config + cantidad editable + subtotal + boton "+ Fila"; simple = 1 fila,
  // solo cantidad).
  function renderSubEstadoBlock(c, s) {
    const label = subEstadoLabel(s);
    const tipo = estadoTipo(s);
    const isPorcionado = tipo === 'porcionado';
    const tamanoConfig = estadoTamanoConfig(s);
    const tamanoLabel = (tamanoConfig !== null) ? `${Number(tamanoConfig).toLocaleString('es-CO')}g` : '?';
    const estado = c._inlineSubConteos[label] || { filas: [], total: 0, tipo };

    const filasHtml = (estado.filas || []).map((f, fi) => {
      const cantStr = (f.cantidad === null || f.cantidad === undefined) ? '' : f.cantidad;
      if (isPorcionado) {
        const sub = subtotalFila(f, 'porcionado', tamanoConfig, c.unidad);
        return `<div style="display:grid;grid-template-columns:1fr 56px 8px 64px 68px 22px;gap:5px;align-items:center;padding:3px 4px">
          <span style="font-size:0.875rem;color:#6e7681">Fila ${fi + 1}</span>
          <span style="font-size:0.875rem;color:#58a6ff;text-align:center;background:#1f6feb15;border:1px solid #1f6feb40;border-radius:4px;padding:3px 0" title="Tamano fijo del estado (config)">${ctx.escHtml(tamanoLabel)}</span>
          <span style="color:#6e7681;font-size:0.875rem;text-align:center">x</span>
          <input type="text" inputmode="numeric" class="rec-fila-cantidad" data-label="${ctx.escHtml(label)}" data-fi="${fi}" value="${ctx.escHtml(String(cantStr))}" placeholder="Cant"
                 style="width:64px;padding:4px;background:#0d1117;border:1px solid #30363d;border-radius:4px;color:#e1e4e8;font-size:0.875rem;text-align:center">
          <span class="rec-fila-subtotal" data-label="${ctx.escHtml(label)}" data-fi="${fi}" style="font-size:0.875rem;color:#8b949e;text-align:right">= ${sub ? fmtNum(sub) : '-'}</span>
          <button type="button" class="rec-fila-del" data-label="${ctx.escHtml(label)}" data-fi="${fi}" title="Quitar fila" style="color:#f85149;font-size:0.9rem;padding:0;background:transparent;border:0;cursor:pointer">×</button>
        </div>`;
      }
      return `<div style="display:grid;grid-template-columns:1fr 80px 22px;gap:5px;align-items:center;padding:3px 4px">
        <span style="font-size:0.875rem;color:#6e7681">Fila ${fi + 1}</span>
        <input type="text" inputmode="numeric" class="rec-fila-cantidad" data-label="${ctx.escHtml(label)}" data-fi="${fi}" value="${ctx.escHtml(String(cantStr))}" placeholder="Cant"
               style="width:80px;padding:4px;background:#0d1117;border:1px solid #30363d;border-radius:4px;color:#e1e4e8;font-size:0.875rem;text-align:center">
        <button type="button" class="rec-fila-del" data-label="${ctx.escHtml(label)}" data-fi="${fi}" title="Quitar fila" style="color:#f85149;font-size:0.9rem;padding:0;background:transparent;border:0;cursor:pointer">×</button>
      </div>`;
    }).join('');

    const noFilas = (estado.filas || []).length === 0
      ? `<div style="padding:4px 4px;font-size:0.875rem;color:#6e7681;font-style:italic">Sin filas. "+ Fila" para agregar.</div>`
      : '';
    const porcioTag = isPorcionado ? ` <span style="font-size:0.875rem;color:#58a6ff">(${ctx.escHtml(tamanoLabel)}/u)</span>` : '';

    return `<div class="rec-sub-block" data-label="${ctx.escHtml(label)}" style="border-top:1px solid #21262d;padding-top:4px;margin-top:4px">
      <div style="display:flex;align-items:center;justify-content:space-between;padding:2px 2px 4px">
        <span style="font-size:0.875rem;color:#c9d1d9;font-weight:600">${ctx.escHtml(label)}${porcioTag}</span>
        <div style="display:flex;align-items:center;gap:8px">
          <span data-rec-estado-total="${ctx.escHtml(label)}" style="font-size:0.875rem;color:#8b949e">Total: <strong style="color:#e1e4e8">${fmtNum(estado.total)}</strong></span>
          <button type="button" class="rec-fila-add" data-label="${ctx.escHtml(label)}" title="Agregar fila a ${ctx.escHtml(label)}" style="font-size:0.875rem;padding:2px 8px;background:#238636;border:1px solid #238636;border-radius:4px;color:#fff;cursor:pointer">+ Fila</button>
        </div>
      </div>
      ${noFilas}${filasHtml}
    </div>`;
  }

  function renderReconteoInline() {
    const c = casos[currentIdx];
    const intento = Number(c.reconteosIntentos || 0) + 1;
    const subEstados = Array.isArray(c.subEstados) ? c.subEstados : [];
    const isMulti = subEstados.length > 0;
    const u = c.unidad ? ' ' + c.unidad : '';

    // Estado local de filas por caso (persiste si el operador cancela y vuelve a
    // entrar). Mismo shape {filas:[{cantidad,tamano?}],total,tipo} que sub_conteos
    // del conteo normal -- permite estados porcionado con multiples filas (+Fila).
    if (isMulti && !c._inlineSubConteos) {
      c._inlineSubConteos = initSubConteos(subEstados);
      // PRECARGA (decision Josse 2026-07-18): si hay conteo original, arranca con
      // ESE valor como default en vez de vacio -- el operador confirma o corrige,
      // no tiene que re-teclear todo desde cero. No rompe el anti-fraude (sigue sin
      // ver el stock del sistema ni el diff exacto, diffDireccion se mantiene oculto).
      if (c.conteoOriginalSubConteos) {
        for (const [label, estadoOriginal] of Object.entries(c.conteoOriginalSubConteos)) {
          if (c._inlineSubConteos[label] && Array.isArray(estadoOriginal?.filas)) {
            c._inlineSubConteos[label] = {
              filas: estadoOriginal.filas.map(f => ({ cantidad: f.cantidad ?? null, tamano: f.tamano ?? null })),
              total: typeof estadoOriginal.total === 'number' ? estadoOriginal.total : 0,
              tipo: c._inlineSubConteos[label].tipo // tipo de la config VIGENTE, no la guardada
            };
          }
        }
      }
    } else if (!isMulti && !c._inlineInputs) {
      c._inlineInputs = { __simple: (c.conteoOriginalSimple !== null && c.conteoOriginalSimple !== undefined) ? String(c.conteoOriginalSimple) : '' };
    }

    // REC-T5 fix propio (cazado en smoke): total/confirmDisabled DEBEN recalcularse
    // en el momento de USARLOS (render inicial Y click en Confirmar), no quedar
    // capturados en una const del closure del render -- las actualizaciones
    // puntuales (actualizarDerivados) mutan c._inlineSubConteos/c._inlineInputs
    // directamente sin volver a llamar renderReconteoInline, asi que una const
    // calculada solo al inicio del render queda DESFASADA del total real visible
    // en pantalla apenas el operador edita algo despues de un +Fila/-Fila.
    const calcularTotal = () => isMulti
      ? Object.values(c._inlineSubConteos).reduce((s, e) => s + (Number(e.total) || 0), 0)
      : (Number(c._inlineInputs.__simple) || 0);
    // CRITICAL bit #5225/#5232: el `|| totalNow <= 0` que vivia aqui hacia
    // IMPOSIBLE confirmar un reconteo cuyo resultado real es CERO (insumo
    // agotado — cotidiano en cocina). El caso quedaba indecidible, el flow
    // nunca terminaba y el operador no podia enviar su conteo. El 0 DIGITADO
    // es una respuesta: lo no-respondido se detecta por vacio (simple) o por
    // cero filas (multi), no por el total.
    const calcularDisabled = () => isMulti
      ? !Object.values(c._inlineSubConteos).some(e => (e.filas || []).length > 0)
      : String(c._inlineInputs.__simple).trim() === '';

    const total = calcularTotal();
    const confirmDisabled = calcularDisabled(total);

    // Body condicional: simple (1 input) vs multi-estado (filas por sub-estado)
    let bodyHtml;
    if (isMulti) {
      bodyHtml = `
        <div style="display:flex;flex-direction:column;gap:1px;margin-bottom:4px">
          ${subEstados.map(s => renderSubEstadoBlock(c, s)).join('')}
        </div>
        <div style="display:flex;justify-content:space-between;font-size:0.875rem;padding:8px 0 2px;border-top:1px solid #21262d;margin-top:8px">
          <strong style="color:#8b949e">Total</strong>
          <strong data-rec-total-display style="color:#f0f6fc">${fmtNum(total)}${u}</strong>
        </div>
      `;
    } else {
      bodyHtml = `
        <label style="display:block;font-size:0.875rem;color:#8b949e;margin-bottom:4px">Cantidad nueva${u ? ` (${ctx.escHtml(c.unidad)})` : ''}</label>
        <input data-rec-inline-simple type="number" min="0" step="any" inputmode="decimal" value="${ctx.escHtml(c._inlineInputs.__simple ?? '')}" placeholder="0"
               style="width:100%;box-sizing:border-box;padding:10px 12px;background:#0d1117;border:1px solid #30363d;border-radius:5px;color:#f0f6fc;font-size:1.05rem;text-align:right;font-weight:600">
      `;
    }

    container.innerHTML = `
      <div class="aa-reconteo" data-aa-mode="reconteo-decisions">
        <div style="padding:12px 16px 4px;font-size:0.875rem;color:#8b949e;display:flex;justify-content:space-between;align-items:center;gap:8px;flex-wrap:wrap">
          <span>Caso ${currentIdx + 1} de ${casos.length} -- ${ctx.escHtml(c.nombre)}</span>
          <span style="padding:1px 7px;background:#a371f71a;color:#a371f7;border-radius:8px;white-space:nowrap">Reconteo ${intento} de 2</span>
        </div>

        <div style="padding:8px 16px 10px">
          <div style="display:flex;gap:8px;align-items:flex-start;margin-bottom:14px">
            <span style="font-size:1.1rem;line-height:1.2">🔁</span>
            <div style="font-size:0.875rem;color:#c9d1d9;line-height:1.5;background:#161b22;border:1px solid #21262d;border-radius:8px;padding:9px 11px;flex:1">
              Cuenta <strong>${ctx.escHtml(c.nombre)}</strong> otra vez. Sin pistas -- solo tu conteo cuenta.
            </div>
          </div>

          ${bodyHtml}

          <div data-rec-status style="margin-top:10px;font-size:0.875rem;min-height:1em"></div>
        </div>

        <footer style="display:flex;align-items:center;justify-content:flex-end;gap:8px;padding:10px 16px 14px;border-top:1px solid #21262d">
          <button data-rec-cancelar-inline type="button"
                  style="padding:7px 14px;background:transparent;border:1px solid #30363d;border-radius:5px;color:#c9d1d9;cursor:pointer;font-size:0.875rem">
            Cancelar
          </button>
          <button data-rec-confirmar-inline type="button" ${confirmDisabled ? 'disabled' : ''}
                  style="padding:7px 16px;background:${confirmDisabled ? '#30363d' : '#238636'};border:1px solid ${confirmDisabled ? '#30363d' : '#238636'};border-radius:5px;color:#fff;cursor:${confirmDisabled ? 'not-allowed' : 'pointer'};font-size:0.875rem;opacity:${confirmDisabled ? 0.55 : 1}">
            Confirmar reconteo
          </button>
        </footer>
      </div>
    `;

    // REC-T5: actualiza SOLO total + estado del boton por selector puntual, sin
    // tocar los <input> -- mismo patron que REC-T1 (reconstruir container en cada
    // keystroke mata el foco). Se usa para el input simple Y para cada fila.
    const actualizarDerivados = () => {
      const totalNow = calcularTotal();
      const disabledNow = calcularDisabled(totalNow);

      const $total = container.querySelector('[data-rec-total-display]');
      if ($total) $total.textContent = `${fmtNum(totalNow)}${u}`;

      const $btn = container.querySelector('[data-rec-confirmar-inline]');
      if ($btn) {
        $btn.disabled = disabledNow;
        $btn.style.background = disabledNow ? '#30363d' : '#238636';
        $btn.style.borderColor = disabledNow ? '#30363d' : '#238636';
        $btn.style.cursor = disabledNow ? 'not-allowed' : 'pointer';
        $btn.style.opacity = disabledNow ? '0.55' : '1';
      }
    };

    if (isMulti) {
      // Cantidad de una fila -> recalcula subtotal fila + total estado + total
      // global, actualizando SOLO esos nodos (no reconstruye, preserva el foco).
      container.querySelectorAll('.rec-fila-cantidad').forEach(input => {
        input.addEventListener('input', (e) => {
          const label = input.dataset.label;
          const fi = Number(input.dataset.fi);
          const estado = c._inlineSubConteos[label];
          if (!estado || !estado.filas[fi]) return;
          estado.filas[fi].cantidad = e.target.value === '' ? null : e.target.value;
          const s = subEstados.find(x => subEstadoLabel(x) === label);
          const tamanoConfig = estadoTamanoConfig(s);
          estado.total = totalEstado(estado, tamanoConfig, c.unidad);

          const $sub = container.querySelector(`.rec-fila-subtotal[data-label="${CSS.escape(label)}"][data-fi="${fi}"]`);
          if ($sub) { const s2 = subtotalFila(estado.filas[fi], estado.tipo, tamanoConfig, c.unidad); $sub.textContent = `= ${s2 ? fmtNum(s2) : '-'}`; }
          const $et = container.querySelector(`[data-rec-estado-total="${CSS.escape(label)}"] strong`);
          if ($et) $et.textContent = fmtNum(estado.total);

          actualizarDerivados();
        });
      });
      // +Fila / quitar fila cambian la ESTRUCTURA (numero de filas) -> requieren
      // rebuild, pero es un click (no keystroke), no hay foco de texto en riesgo.
      container.querySelectorAll('.rec-fila-add').forEach(btn => {
        btn.addEventListener('click', () => {
          c._inlineSubConteos[btn.dataset.label].filas.push({ cantidad: null, tamano: null });
          renderReconteoInline();
        });
      });
      container.querySelectorAll('.rec-fila-del').forEach(btn => {
        btn.addEventListener('click', () => {
          const label = btn.dataset.label;
          const fi = Number(btn.dataset.fi);
          const estado = c._inlineSubConteos[label];
          estado.filas.splice(fi, 1);
          const s = subEstados.find(x => subEstadoLabel(x) === label);
          estado.total = totalEstado(estado, estadoTamanoConfig(s), c.unidad);
          renderReconteoInline();
        });
      });
    } else {
      const $s = container.querySelector('[data-rec-inline-simple]');
      if ($s) {
        $s.addEventListener('input', (e) => {
          c._inlineInputs.__simple = e.target.value;
          actualizarDerivados();
        });
      }
    }

    container.querySelector('[data-rec-cancelar-inline]').addEventListener('click', () => {
      // REC-T4 (bit #3180): Cancelar va directo a la lista (1 sola interaccion),
      // no a la vista de detalle intermedia.
      delete c._inlineInputs;
      delete c._inlineSubConteos;
      currentView = 'lista';
      render();
    });

    container.querySelector('[data-rec-confirmar-inline]').addEventListener('click', async () => {
      // REC-T5 fix propio: recalcular FRESCO en el momento del click, no usar la
      // const `total` capturada al inicio del render -- las actualizaciones
      // puntuales (+Fila, editar cantidad) mutan el estado sin volver a renderizar,
      // asi que `total` quedaria desfasado del valor REALMENTE visible en pantalla
      // (cazado en smoke: total mostrado != total enviado tras un +Fila seguido de
      // editar una fila nueva).
      const totalReal = calcularTotal();
      if (calcularDisabled(totalReal)) return;
      const $st = container.querySelector('[data-rec-status]');
      if ($st) { $st.style.color = '#58a6ff'; $st.textContent = 'Registrando reconteo...'; }

      // REC-T5: subConteos en el MISMO shape {filas,total,tipo} que el conteo
      // normal -- ReconteoApiClient.recount ya lo incluye en el POST body.
      const subConteos = isMulti ? c._inlineSubConteos : null;
      const res = await ReconteoApiClient.recount(c.logId, totalReal, subConteos);

      if (!res.ok) {
        // OPS-RECONTEO-P2: 409 = decision ya terminal del lado backend (defensa en
        // profundidad -- el boton ya deberia estar oculto por esDecisionTerminal,
        // pero si por alguna razon se alcanza igual, mensaje claro en vez de
        // "error desconocido"/generico que el operador confunde con falla de red.
        if (res.code === 409 || String(res.code) === '409') {
          if ($st) { $st.style.color = '#d29922'; $st.textContent = 'Este caso ya fue decidido y no se puede recontar. Vuelve a la lista para ver el estado actual.'; }
          return;
        }
        // CRITICAL bit #5240 (opcion A de Ares): mientras la API no acepte el 0
        // (CONTEO-FIX-T2 pendiente: DTO Range 0.001 + guard <=0), el 400 de un
        // reconteo en cero se traduce a lenguaje del operador — que sepa que SU
        // conteo es valido y el arreglo viene en camino, en vez de un codigo pelado.
        // Este bloque se vuelve inalcanzable cuando el backend acepte 0: no estorba.
        if (totalReal === 0 && (res.code === 400 || String(res.code) === '400' || /mayor que cero|> 0/i.test(String(res.error || '')))) {
          if ($st) { $st.style.color = '#d29922'; $st.textContent = 'Contaste 0 y es valido (insumo agotado), pero el servidor todavia no acepta cero — el arreglo esta en camino. Avisale al administrador para cerrar este caso; tu conteo de los demas insumos no se pierde.'; }
          return;
        }
        // CrudResult canonico: res.error = mensaje string, res.code = HTTP status o code custom.
        const msg = (typeof res.error === 'string' ? res.error : null) || res.error?.message || res.code || 'error desconocido';
        if ($st) { $st.style.color = '#f85149'; $st.textContent = `Error: ${msg}`; }
        return;
      }

      // T5 state machine: actualiza estado del caso + decide proximo view.
      c.reconteos = Array.isArray(c.reconteos) ? c.reconteos : [];
      c.reconteos.push({ cantidadNueva: totalReal, subConteos, cuadra: res.data.cuadra, ts: Date.now() });
      c.conteoRecontado = totalReal;
      c.conteoReal = totalReal; // el ultimo conteo del operador queda como conteo "oficial" del caso
      c.reconteosIntentos = Number(res.data.reconteosIntentos ?? c.reconteos.length);
      c.intentosRestantes = Number(res.data.intentosRestantes ?? Math.max(0, MAX_INTENTOS - c.reconteosIntentos));
      if (typeof res.data.diffSignedNuevo === 'number') c.diffSigned = res.data.diffSignedNuevo;

      ctx.emit('mode-action', {
        mode: 'reconteo-decisions',
        action: 'reconteo-inline-confirmed',
        data: { caso: { ...c }, res: res.data }
      });

      delete c._inlineInputs;
      delete c._inlineSubConteos;

      if (res.data.cuadra) {
        // Cuadra -> auto-accept (decision='aceptar', motivo='reconteo_cuadra').
        c.decision = 'aceptar';
        if (!c.motivo) c.motivo = 'reconteo_cuadra';
        if ($st) { $st.style.color = '#3fb950'; $st.textContent = 'Cuadra perfecto. Reconteo aceptado.'; }
        // Si hay onCasoDecidido (callback panel) lo notifica como decision normal del caso.
        if (onCasoDecidido) {
          try { onCasoDecidido({ caso: { ...c }, ok: true, response: { status: 200, body: { source: 'reconteo-inline-auto-accept' } } }); }
          catch (e) { console.error('[reconteo-decisions] onCasoDecidido threw:', e); }
        }
        // Si quedan pendientes en otros casos, salta al siguiente; sino vuelve a lista.
        setTimeout(() => {
          const next = casos.findIndex((cc, i) => i > currentIdx && cc.decision === 'pendiente');
          const anyPend = casos.findIndex(cc => cc.decision === 'pendiente');
          if (next >= 0) { currentIdx = next; currentView = 'detalle'; }
          else if (anyPend >= 0) { currentIdx = anyPend; currentView = 'detalle'; }
          else { currentView = 'lista'; }
          render();
          if (todasDecididas()) {
            ctx.emit('mode-action', { mode: 'reconteo-decisions', action: 'completed', data: { casos: casos.map(x => ({ ...x })) } });
            if (onDecisionsComplete) {
              try { onDecisionsComplete({ casos: casos.map(x => ({ ...x })) }); }
              catch (e) { console.error('[reconteo-decisions] onDecisionsComplete threw:', e); }
            }
          }
        }, 900);
      } else if (c.intentosRestantes > 0) {
        // Sigue mal pero hay intentos -> re-render mismo view (indicador "Reconteo N de 2" se actualiza).
        if ($st) { $st.style.color = '#d29922'; $st.textContent = `Aun no cuadra. Te queda ${c.intentosRestantes} ${c.intentosRestantes === 1 ? 'intento' : 'intentos'}. Cuenta otra vez.`; }
        setTimeout(() => { renderReconteoInline(); }, 1300);
      } else {
        // Agotado: 2 intentos sin cuadrar -> volver a detalle. renderDetalle ocultara
        // el boton "Volver a contar"; solo quedan Justificar / Escalar admin.
        if ($st) { $st.style.color = '#d29922'; $st.textContent = 'Sin mas intentos. Justifica o escala.'; }
        setTimeout(() => { currentView = 'detalle'; render(); }, 1400);
      }
    });
  }

  // ============== Decision + POST webhook ==============

  function motivoLabel(value) {
    const m = MOTIVOS.find(x => x.value === value);
    return m ? m.label : value;
  }

  async function decidir(decision) {
    const c = casos[currentIdx];
    c.decision = decision;

    const $status = container.querySelector('[data-rec-status]');
    if ($status) { $status.style.color = '#79c0ff'; $status.textContent = 'Registrando decision...'; }

    // Contract webhook /reconteo/decision (Argy-A T2): el log ya existe (lo inserto
    // analyze con decision=null); el decision solo referencia log_id + la decision.
    const body = {
      log_id: c.logId,
      decision,
      motivo_tipado: decision === 'aceptar' ? c.motivo : null,
      justificacion: decision === 'aceptar' ? (c.justificacion || '') : null
    };

    let ok = false;
    let response = null;
    try {
      const res = await fetchQueCambia(decisionWebhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const rawText = await res.text();
      let parsed = null;
      try { parsed = JSON.parse(rawText); } catch { parsed = rawText; }
      response = { status: res.status, body: parsed };
      ok = res.ok;
    } catch (err) {
      response = { error: err?.message || String(err) };
      ok = false;
    }

    ctx.emit('mode-action', {
      mode: 'reconteo-decisions',
      action: 'decision',
      data: { caso: { ...c }, ok, response }
    });
    if (onCasoDecidido) {
      try { onCasoDecidido({ caso: { ...c }, ok, response }); }
      catch (e) { console.error('[reconteo-decisions] onCasoDecidido threw:', e); }
    }

    if (!ok) {
      // La decision queda registrada localmente igual; el webhook se reintenta al
      // cerrar/enviar. Avisamos sin bloquear el flujo del operador.
      console.warn('[reconteo-decisions] POST decision fallo:', response);
      const $st = container.querySelector('[data-rec-status]');
      if ($st) {
        $st.style.color = '#d29922';
        $st.textContent = 'Decision guardada localmente (sin conexion con el servidor).';
      }
    }

    // Avanzar: si quedan pendientes, ir al siguiente; sino volver a la lista.
    const next = casos.findIndex((cc, i) => i > currentIdx && cc.decision === 'pendiente');
    const anyPend = casos.findIndex(cc => cc.decision === 'pendiente');
    if (next >= 0) {
      currentIdx = next;
      currentView = 'detalle';
    } else if (anyPend >= 0) {
      currentIdx = anyPend;
      currentView = 'detalle';
    } else {
      currentView = 'lista';
    }
    render();

    if (todasDecididas()) {
      ctx.emit('mode-action', { mode: 'reconteo-decisions', action: 'completed', data: { casos: casos.map(x => ({ ...x })) } });
      if (onDecisionsComplete) {
        try { onDecisionsComplete({ casos: casos.map(x => ({ ...x })) }); }
        catch (e) { console.error('[reconteo-decisions] onDecisionsComplete threw:', e); }
      }
    }
  }

  function finish() {
    ctx.emit('mode-action', { mode: 'reconteo-decisions', action: 'completed', data: { casos: casos.map(x => ({ ...x })) } });
    if (onDecisionsComplete) {
      try { onDecisionsComplete({ casos: casos.map(x => ({ ...x })) }); }
      catch (e) { console.error('[reconteo-decisions] onDecisionsComplete threw:', e); }
    }
    ctx.clear();
  }

}

function expandContainer(container) {
  Object.assign(container.style, {
    maxHeight: '600px',
    opacity: '1',
    margin: '12px 0',
    border: '1px solid #a371f7',
    padding: '0'
  });
}
